import { unum } from '@oliasoft-open-source/units';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { HierarchyLevelType, IObject, SecurityClassification } from 'common/interfaces/hierarchy-entities';
import { entityTypeMap } from 'common/table-viewer/utils';
import { apiCallBegan } from 'client/store/middleware/api/api';
import { EntityType } from 'common/enums/entity-enums';
import { designUpdated } from 'client/store/entities/hierarchy/design/design';

const formatValuesToUserActiveUnitTemplate = (groupedConfiguration: {
  [index: string]: { unit: string }[];
}) => (i: IObject) => {
  const emptyData = !Array.isArray(i.data);
  if (emptyData) {
    return i;
  }

  const pastedUnitsRow = i.data[0] || {};
  const keys = Object.keys(pastedUnitsRow);
  // @ts-ignore
  const [, ...dataRows] = i.data;  // skip index 0 (units row) — it must not be converted and re-prepended
  const map = entityTypeMap.get(i.type as EntityType);
  const unitsRow: {
    [index: string]: string;
  } = {};

  const convertedData = dataRows.map(i => {
    const valueInStorageUnit = { ...i };
    for (const key of keys) {
      const storageUnit = map?.get(key)?.unit;  // I changed my mind we always will store in storage unit, saving ms on skipping conversation on ui is not work of mental overhead
      const quantity = map?.get(key)?.quantity;
      const toUnit = groupedConfiguration?.[quantity]?.[0]?.unit;
      const hasDeffinedUnit = toUnit && storageUnit;
      valueInStorageUnit[key] = hasDeffinedUnit ? unum(valueInStorageUnit[key], toUnit, storageUnit) : valueInStorageUnit[key];
      if (!unitsRow[key]) {
        unitsRow[key] = toUnit;
      }
    }
    return valueInStorageUnit;
  });

  return { ...i, data: [unitsRow, ...convertedData] };
};

const object = createSlice({
  name: 'object',
  initialState: {
    isLoading: false,
    error: null,
    objects: {} as { [nodeId: string]: IObject[] },
    associatedObjects: {} as { [objectid: string]: any },
    blobUrl: '',
    isLoadingRaw: false,
    raw: null,
    isLoadingUnpack: false,
    unPackError: null,
    errorUnpack: false,
    preUnpackData: [],
    isLoadingPreUnpack: false,
    preUnpackError: null,
    isLoadingUpdate: false,
    updateError: null,
  },
  reducers: {
    objectsRequested: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    objectsRecieved: (state, action) => {
      const { groupedConfiguration, nodeid, response } = action.payload;
      state.isLoading = false;
      const formatter = formatValuesToUserActiveUnitTemplate(groupedConfiguration);

      state.objects[nodeid] = response.rows.reduce((acc: IObject[], object: IObject) => {
        acc.push(formatter(object));
        const { associateddata, associatedobjectid } = object;
        if (associateddata) {
          state.associatedObjects[object.objectid] = { associatedobjectid, ...associateddata };
        }
        return acc;
      }, [] as IObject[]);
    },
    objectsByTypeAndParentIdReceived: (state, action) => {
      const { response } = action.payload;
      state.isLoading = false;

      for (const object of response.rows) {
        if (!state.objects[object.nodeid]) {
          state.objects[object.nodeid] = [];
        }
        state.objects[object.nodeid].push(object);
      }
    },
    classificationLevelUpdated: (state, action) => {
      state.isLoading = false;
      const { nodeid, objectid, classificationlevel } = action.payload;
      state.objects[nodeid] = state.objects[nodeid].map(object => (object.objectid !== objectid ? object : {
        ...object,
        classificationlevel,
      }));
    },
    objectsFailed: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    objectsReset: (state) => {
      state.objects = {};
      state.associatedObjects = {};
    },
    rawRequested: (state) => {
      state.isLoadingRaw = true;
      state.error = null;
      state.raw = null;
    },
    rawSucceeded: (state, action) => {
      state.isLoadingRaw = false;
      state.raw = action.payload;
    },
    rawFailed: (state, action) => {
      state.isLoadingRaw = false;
      state.error = action.payload;
      state.raw = null;
    },
    unpackRequested: (state) => {
      state.isLoadingUnpack = true;
      state.unPackError = null;
    },
    unpackSucceeded: (state) => {
      state.isLoadingUnpack = false;
    },
    unpackFailed: (state, action) => {
      state.isLoadingUnpack = false;
      state.unPackError = action.payload;
    },
    preUnpackRequested: (state) => {
      state.isLoadingPreUnpack = true;
      state.preUnpackError = null;
    },
    preUnpackSucceeded: (state, action) => {
      state.isLoadingPreUnpack = false;
      state.preUnpackData = action.payload;
    },
    preUnpackFailed: (state, action) => {
      state.isLoadingUnpack = false;
      state.preUnpackError = action.payload;
    },
    upsertRequested: (state) => {
      state.isLoadingUpdate = true;
    },
    upsertSucceeded: (state, action) => {
      state.isLoadingUpdate = false;
      const index = state.objects[action.payload.object.nodeid].findIndex(object => object.objectid === action.payload.objectid);
      state.objects[action.payload.object.nodeid][index] = action.payload.object;
    },
    associatedDataSet: (state, action) => {
      state.isLoadingUpdate = false;
      const { objectid, nodeid, associatedData, associatedobjectid } = action.payload;
      state.associatedObjects = {
        ...state.associatedObjects,
        [objectid]: { ...associatedData, associatedobjectid, },
      };
      const nodeObjects = state.objects[nodeid];
      if (nodeObjects) {
        const idx = nodeObjects.findIndex(obj => obj.objectid === objectid);
        if (idx !== -1) {
          nodeObjects[idx] = { ...nodeObjects[idx], associateddata: associatedData };
        }
      }
    },
    upsertFailed: (state, action) => {
      state.isLoadingUpdate = false;
      state.updateError = action.payload;
    },
    deleteSucceeded: (state, action: PayloadAction<IObject>) => {
      const nodeObjects = state.objects[action.payload.nodeid];
      if (!nodeObjects) return;
      state.objects[action.payload.nodeid] = nodeObjects.filter(obj => obj.objectid !== action.payload.objectid);
    },
    addSucceeded: (state, action) => {
      state.isLoadingUpdate = false;
      state.objects[action.payload.nodeid].push(action.payload);
    },
    updateNameSucceeded: (state, action) => {
      state.isLoadingUpdate = false;
      const { objectid, name, nodeid } = action.payload;
      const nodeObjects = state.objects[nodeid];
      if (!nodeObjects) return;
      const index = nodeObjects.findIndex(obj => obj.objectid === objectid);
      if (index !== -1) {
        nodeObjects[index].name = name;
      }
    },
    clearDataSucceeded: (state, action) => {
      const { objectid, nodeid } = action.payload;
      const nodeObjects = state.objects[nodeid];
      if (!nodeObjects) return;
      const index = nodeObjects.findIndex(obj => obj.objectid === objectid);
      if (index !== -1) {
        nodeObjects[index] = action.payload;
      }
    },
    moveSucceeded: (state, action) => {
      const {
        objectid, nodeid, oldNodeid, name, classificationlevel,
      } = action.payload;

      const sourceObjects = state.objects[oldNodeid] ?? [];
      const movedObject = sourceObjects.find((sourceObject) => sourceObject.objectid === objectid);
      if (!movedObject) {
        return;
      }
      state.objects[oldNodeid] = sourceObjects.filter((sourceObject) => sourceObject.objectid !== objectid);

      const targetObjects = state.objects[nodeid] ?? [];
      state.objects[nodeid] = [...targetObjects, { ...movedObject, nodeid, name, classificationlevel }];
    },
  },
  extraReducers: (builder) => builder
    .addCase(designUpdated, (state, action: PayloadAction<any>) => {
      const { nodeid, well_status } = action.payload;
      const nodeObjects = state.objects[nodeid];
      if (!nodeObjects) return;
      state.objects[nodeid] = nodeObjects.map(object => {
        if (object.type !== EntityType.ProjectData) return object;

        const isProjectDataTable = Array.isArray(object.data) && object.data.length > 0;
        if (isProjectDataTable) {
          //@ts-ignore
          const [unitsRow, valuesRow, ...restRows] = object.data;
          return {
            ...object,
            data: [unitsRow, { ...valuesRow, wellStatus: well_status }, ...restRows],
          };
        }

        const isEmptyTablePlaceholder = !Array.isArray(object.data) && Array.isArray(object.data?.rows);
        if (isEmptyTablePlaceholder) {
          return {
            ...object,
            data: { ...object.data, wellStatus: well_status },
          };
        }
        return object;
      });
  })
});

export const {
  objectsRequested,
  objectsRecieved,
  objectsByTypeAndParentIdReceived,
  objectsFailed,
  objectsReset,
  rawRequested,
  rawSucceeded,
  rawFailed,
  unpackRequested,
  unpackSucceeded,
  unpackFailed,
  preUnpackRequested,
  preUnpackSucceeded,
  preUnpackFailed,
  upsertSucceeded,
  associatedDataSet,
  upsertFailed,
  upsertRequested,
  classificationLevelUpdated,
  deleteSucceeded,
  addSucceeded,
  updateNameSucceeded,
  clearDataSucceeded,
  moveSucceeded,
} = object.actions;

export default object.reducer;

export const getObjectsOfNode = (nodeid: string, unitTemplateQuantitiesGrouped?: any) => apiCallBegan({
  url: `/getObjects?nodeid=${nodeid}`,
  method: 'GET',
  onStart: objectsRequested.type,
  // @ts-ignore
  onSuccess: (response) => {
    return {
      type: objectsRecieved.type,
      payload: { response, nodeid, groupedConfiguration: unitTemplateQuantitiesGrouped },
    };
  },
  onError: objectsFailed.type,
});

export const getObjectsByTypeAndParentId = (objecttype: EntityType, parenttype: HierarchyLevelType, parentid: string) => apiCallBegan({
  url: `/getObjects/${objecttype}?parenttype=${parenttype}&parentid=${parentid}`,
  method: 'GET',
  onStart: objectsRequested.type,
  // @ts-ignore
  onSuccess: (response) => ({
    type: objectsByTypeAndParentIdReceived.type,
    payload: { response, parentid, objecttype },
  }),
  onError: objectsFailed.type,
});

// @ts-ignore
export const getRaw = (blobName: string) => apiCallBegan({
  url: `/object/raw?blobName=${blobName}`,
  method: 'GET',
  onStart: rawSucceeded.type,
  // @ts-ignore
  onSuccess: (response) => ({
    type: rawSucceeded.type,
    payload: response,
  }),
  onError: rawFailed.type,
});

export const unpackObject = ({
  archiveid, country, selectedDesingIds, objectid,
}: any) => apiCallBegan({
  url: `/object/unpack/${objectid}?archiveid=${archiveid}`,
  method: 'PUT',
  onStart: unpackRequested.type,
  data: { country, selectedDesingIds },
  // @ts-ignore
  onSuccess: (response) => ({
    type: unpackSucceeded.type,
    payload: response,
  }),
  onError: unpackFailed.type,
});

export const deleteObject = (data: Partial<IObject>) => apiCallBegan({
  url: `/object/${data.objectid}`,
  method: 'DELETE',
  onSuccess: () => ({
    type: deleteSucceeded.type,
    payload: data,
  }),
  toastOnError: true,
  onError: objectsFailed.type,
});

export const upsert = (data: Partial<IObject>, stateUpdateCb: Function) => apiCallBegan({
  url: `/object?type=${data?.type}`,
  method: 'PATCH',
  data,
  // @ts-ignore
  onSuccess: (response) => {
    if (stateUpdateCb) {
      stateUpdateCb(data);
    }
    return {
      type: upsertSucceeded.type,
      payload: {
        objectid: response,
        object: data,
      },
    };
  },
  onStart: upsertRequested.type,
  onError: upsertFailed.type,
});

const associatedDataIdFieldByType: { [index: string]: string } = {
  [EntityType.SurveyRun]: 'surveyrunsassociateddataid',
  [EntityType.SurveyProgram]: 'surveyprogramassociateddataid',
};

export const upsertAssociatedData = (objectid: string, nodeid: string, data: any, type: string, designid: string) => apiCallBegan({
  url: `/object/${objectid}/associated?type=${type}&designid=${designid}`,
  method: 'PATCH',
  data,
  onStart: upsertRequested.type,
  onSuccess: ({ associatedobjectid }: { associatedobjectid: string }) => {
    const idField = associatedDataIdFieldByType[type];
    return {
      type: associatedDataSet.type,
      payload: {
        objectid,
        nodeid,
        associatedData: idField ? { ...data, [idField]: associatedobjectid } : data,
        associatedobjectid,
      },
    };
  },
  onError: upsertFailed.type,
});

export const updateMetadata = (data: Partial<IObject>, stateUpdateCb: Function) => apiCallBegan({
  url: `/object/${data.objectid}/metadata`,
  method: 'PATCH',
  data,
  // @ts-ignore
  onSuccess: (response) => {
    if (stateUpdateCb) {
      stateUpdateCb(data);
    }
    return {
      type: upsertSucceeded.type,
      payload: {
        objectid: response,
        object: data,
      },
    };
  },
  onStart: upsertRequested.type,
  onError: upsertFailed.type,
});

export const preUnpackObject = ({ objectid, archiveid, country }: { objectid: string, archiveid: string, country: string }) => apiCallBegan({
  url: `/object/preunpack/${objectid}?archiveid=${archiveid}&country=${country}`,
  method: 'GET',
  onStart: preUnpackRequested.type,
  // @ts-ignore
  onSuccess: (response) => ({
    type: preUnpackSucceeded.type,
    payload: response,
  }),
  onError: preUnpackFailed.type,
});

export const updateObjectClassificationLevel = (data: { objectid: string, nodeid: string, classificationlevel: SecurityClassification }) => apiCallBegan({
  url: '/object/classificationlevel',
  method: 'PUT',
  data,
  onStart: objectsRequested.type,
  // @ts-ignore
  onSuccess: (response) => ({
    type: classificationLevelUpdated.type,
    payload: response,
  }),
  onError: objectsFailed.type,
});

export const addStructuredData = (nodeData: any, designid: string) => {
  return apiCallBegan({
    url: `/object/entity?designid=${designid}`,
    method: 'POST',
    onStart: objectsRequested.type,
    data: nodeData,
    // @ts-ignore
    onSuccess: (response) => ({
      type: addSucceeded.type,
      payload: response,
    }),
    onError: objectsFailed.type,
  });
};

export const updateName = (data: Partial<IObject>) => apiCallBegan({
  url: `/object/${data.objectid}/name`,
  method: 'PATCH',
  data,
  onSuccess: (response: Partial<IObject>) => {
    return {
      type: updateNameSucceeded.type,
      payload: response,
    };
  },
  onStart: upsertRequested.type,
  onError: upsertFailed.type,
});

export const moveObjectToNode = (data: {
  objectid: string, nodeid: string, oldNodeid: string, keepBoth?: boolean, successMessage?: string,
}) => apiCallBegan({
  url: `/object/${data.objectid}/node`,
  method: 'PATCH',
  data: { objectid: data.objectid, nodeid: data.nodeid, keepBoth: data.keepBoth },
  toastOnSuccess: !!data.successMessage,
  successMessage: data.successMessage,
  onSuccess: (response: Partial<IObject>) => ({
    type: moveSucceeded.type,
    payload: { ...response, oldNodeid: data.oldNodeid },
  }),
  onStart: upsertRequested.type,
  onError: upsertFailed.type,
});

export const clearData = (data: Partial<IObject>) => {
  const { name, ...rest } = data;
  return apiCallBegan({
    url: `/object/${data.objectid}/reset`,
    method: 'PATCH',
    data: rest,
    onSuccess: (response: Partial<IObject>) => {
      return {
        type: clearDataSucceeded.type,
        payload: response,
      };
    },
    onStart: upsertRequested.type,
    onError: upsertFailed.type,
  });
};
